import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Save, RotateCcw, Eye, Code, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import DOMPurify from 'dompurify';

export default function EmailTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/email-templates');
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleExpand = (key) => {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    const tmpl = templates.find(t => t.template_key === key);
    if (tmpl) setEditData({ subject: tmpl.subject, body_html: tmpl.body_html });
  };

  const handleSave = async (key) => {
    setSaving(true);
    try {
      await api.put(`/email-templates/${key}`, { subject: editData.subject, body_html: editData.body_html, is_active: true });
      toast.success('Template saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleReset = async (key) => {
    try {
      await api.post(`/email-templates/${key}/reset`);
      toast.success('Template reset to default');
      load();
      setExpanded(null);
    } catch { toast.error('Reset failed'); }
  };

  return (
    <div className="space-y-4" data-testid="email-templates-page">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Email Templates</h1>
        <p className="text-sm text-slate-500">Customize outbound email templates for your company</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-xs text-amber-800">
          <strong>Template Variables:</strong> Use <code className="bg-amber-100 px-1 rounded">{'{company_name}'}</code>, <code className="bg-amber-100 px-1 rounded">{'{user_name}'}</code>, <code className="bg-amber-100 px-1 rounded">{'{lead_name}'}</code>, <code className="bg-amber-100 px-1 rounded">{'{email}'}</code>, <code className="bg-amber-100 px-1 rounded">{'{password}'}</code>, <code className="bg-amber-100 px-1 rounded">{'{role}'}</code> in your templates. All emails include "Generated by Transsolution" footer automatically.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {templates.map(tmpl => (
            <div key={tmpl.template_key} className="bg-white border rounded-lg overflow-hidden" data-testid={`template-${tmpl.template_key}`}>
              <button onClick={() => toggleExpand(tmpl.template_key)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Mail size={14} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{tmpl.label}</p>
                    <p className="text-[11px] text-slate-500">{tmpl.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {tmpl.is_default === false && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Customized</span>
                  )}
                  {expanded === tmpl.template_key ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                </div>
              </button>

              {expanded === tmpl.template_key && (
                <div className="border-t px-4 py-4 space-y-3">
                  <div>
                    <label className="form-label">Subject Line</label>
                    <input type="text" value={editData.subject || ''} onChange={e => setEditData({ ...editData, subject: e.target.value })}
                      className="form-input text-sm" data-testid={`template-subject-${tmpl.template_key}`} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="form-label mb-0">Email Body (HTML)</label>
                      <button onClick={() => setPreviewKey(previewKey === tmpl.template_key ? null : tmpl.template_key)}
                        className="text-xs text-slate-500 hover:text-orange-600 flex items-center gap-1">
                        {previewKey === tmpl.template_key ? <><Code size={12} /> Code</> : <><Eye size={12} /> Preview</>}
                      </button>
                    </div>
                    {previewKey === tmpl.template_key ? (
                      <div className="border rounded-md p-4 bg-white min-h-[150px] text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editData.body_html || '') }} />
                    ) : (
                      <textarea value={editData.body_html || ''} onChange={e => setEditData({ ...editData, body_html: e.target.value })}
                        className="form-input text-xs font-mono min-h-[150px]" data-testid={`template-body-${tmpl.template_key}`} />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={() => handleReset(tmpl.template_key)} data-testid={`template-reset-${tmpl.template_key}`}>
                      <RotateCcw size={12} className="mr-1" /> Reset to Default
                    </Button>
                    <Button size="sm" onClick={() => handleSave(tmpl.template_key)} disabled={saving} data-testid={`template-save-${tmpl.template_key}`}>
                      <Save size={12} className="mr-1" /> {saving ? 'Saving...' : 'Save Template'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Footer notice */}
      <div className="bg-slate-50 border rounded-lg p-4">
        <p className="text-xs text-slate-500 text-center">
          All outbound emails automatically include the <strong>"Generated by Transsolution"</strong> footer. This cannot be removed.
        </p>
        <div className="mt-2 p-2 bg-white border rounded text-center">
          <p className="text-[11px] text-slate-400">Generated by <strong className="text-slate-600">Transsolution</strong> — Enterprise Logistics Accounting ERP</p>
          <p className="text-[9px] text-slate-300">This is an automated message. Please do not reply directly.</p>
        </div>
      </div>
    </div>
  );
}
